import { Buffer } from 'buffer';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';

import { useSpdCore } from '../../common/hooks/useSpdCore';

interface InstructionViewProps {
  /**
   * Nazwa pliku HTML pobieranego z backendu (np. `szafir-instalacja.html`).
   * Plik musi znajdować się w `spd-core/docs/final/html/` – endpoint
   * `documentation/byFilename` jest chroniony logowaniem.
   */
  filename: string;
}

export const InstructionView: React.FC<InstructionViewProps> = ({
  filename,
}) => {
  const spd = useSpdCore();
  const containerRef = useRef<HTMLDivElement>(null);
  const [content, setContent] = useState('');
  const [zoom, setZoom] = useState<{ src: string; alt: string } | null>(null);

  useEffect(() => {
    let active = true;
    setContent('');
    spd.docs
      .getForFilename(filename)
      .then(doc => {
        if (active) {
          setContent(Buffer.from(doc.content, 'base64').toString());
        }
      })
      .catch(() => {
        if (active) {
          setContent('<p>Nie udało się wczytać instrukcji.</p>');
        }
      });
    return () => {
      active = false;
    };
  }, [spd.docs, filename]);

  const handleZoom = useCallback((src: string, alt: string) => {
    setZoom({ src, alt });
  }, []);

  // Powiększanie obrazków obsługujemy przez delegację zdarzeń na stałym
  // kontenerze – dzięki temu działa niezależnie od tego, ile razy React
  // ponownie wyrenderuje wstrzykniętą treść.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) {
      return;
    }
    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target instanceof HTMLImageElement) {
        handleZoom(target.src, target.alt);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (
        target instanceof HTMLImageElement &&
        (event.key === 'Enter' || event.key === ' ')
      ) {
        event.preventDefault();
        handleZoom(target.src, target.alt);
      }
    };
    root.addEventListener('click', onClick);
    root.addEventListener('keydown', onKeyDown);
    return () => {
      root.removeEventListener('click', onClick);
      root.removeEventListener('keydown', onKeyDown);
    };
  }, [handleZoom]);

  // Po każdej zmianie treści oznaczamy obrazki jako fokusowalne klawiaturą.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) {
      return;
    }
    root.querySelectorAll('img').forEach(img => {
      img.setAttribute('role', 'button');
      img.setAttribute('tabindex', '0');
    });
  }, [content]);

  return (
    <>
      <div
        ref={containerRef}
        className="m-5 instruction-view"
        dangerouslySetInnerHTML={{ __html: content }}
      ></div>

      <Modal show={!!zoom} onHide={() => setZoom(null)} fullscreen centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 text-muted">{zoom?.alt}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-2">
          <img src={zoom?.src} alt={zoom?.alt} className="img-fluid" />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default InstructionView;
